/**
 * Ziel-Route über den Login hinweg festhalten (#504).
 *
 * Seit es geschützte Routen neben `/desktop` gibt (`/chat`), darf ein Deep-Link
 * den Login nicht überleben, indem er ihn ignoriert: wer abgemeldet `/chat`
 * aufruft, soll nach der Anmeldung dort landen und nicht auf dem Desktop.
 *
 * `sessionStorage` statt React-State, weil der Entra-Login ein voller
 * Browser-Redirect ist — jeder In-Memory-Zustand ist danach weg.
 *
 * Das Ziel trägt auch den Hash: Desktop-Deep-Links stehen dort (`#/w=…`,
 * #1135). `/#/w=…` landet auf der Login-Route und wird von dort auf den
 * Desktop weitergeleitet — ohne gemerkten Hash ging der Link dabei verloren,
 * angemeldet wie abgemeldet.
 */
const POST_LOGIN_TARGET_KEY = 'cdb-post-login-target'

export const DEFAULT_POST_LOGIN_TARGET = '/desktop'

/**
 * Nur app-interne, absolute Pfade. Ein `//host`-Pfad wäre protokoll-relativ und
 * damit ein Open Redirect auf eine fremde Domain.
 */
function isInternalPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//')
}

/** Fenster-Deep-Links des Desktops (`deep-link-service`): aktuelles und altes Format. */
const DESKTOP_DEEP_LINK_PREFIXES = ['#/w=', '#/windows=']

export function isDesktopDeepLinkHash(hash: string): boolean {
  return DESKTOP_DEEP_LINK_PREFIXES.some((prefix) => hash.startsWith(prefix) && hash.length > prefix.length)
}

/**
 * Ein Desktop-Deep-Link auf der Login-Route (`/#/w=…`) wird zum Ziel nach dem
 * Login — `/desktop#/w=…` (#1135). Jeder andere Hash bleibt unbeachtet, vor
 * allem die Antwort des Entra-Redirects (`#code=…`) auf `/auth/callback`.
 */
export function rememberDesktopDeepLink(hash: string): void {
  if (!isDesktopDeepLinkHash(hash)) return
  rememberPostLoginTarget(`${DEFAULT_POST_LOGIN_TARGET}${hash}`)
}

export function rememberPostLoginTarget(path: string): void {
  try {
    if (!isInternalPath(path)) return
    // Der Login selbst ist kein sinnvolles Ziel.
    if (path === '/' || path.startsWith('/auth/callback')) return
    sessionStorage.setItem(POST_LOGIN_TARGET_KEY, path)
  } catch {
    // sessionStorage kann blockiert sein — dann bleibt es beim Default.
  }
}

/**
 * Startseite für einen Nutzer ohne gemerktes Ziel. Wer keinen Desktop hat, aber
 * PDB, gehört in den Chat — sonst landete er auf einer Route, die ihn sofort
 * wieder wegschickt (#504).
 */
export function homeFor(allowedApplications: string[]): string {
  if (allowedApplications.includes('webos')) return DEFAULT_POST_LOGIN_TARGET
  if (allowedApplications.includes('pdb')) return '/chat'
  return DEFAULT_POST_LOGIN_TARGET
}

/**
 * Liefert das gemerkte Ziel und verbraucht es. Ohne Merkposten — oder wenn der
 * Nutzer das gemerkte Ziel gar nicht betreten darf — die passende Startseite.
 */
export function consumePostLoginTarget(allowedApplications: string[] = []): string {
  const home = homeFor(allowedApplications)
  try {
    const target = sessionStorage.getItem(POST_LOGIN_TARGET_KEY)
    sessionStorage.removeItem(POST_LOGIN_TARGET_KEY)
    if (target && isInternalPath(target) && mayEnter(target, allowedApplications)) {
      return target
    }
  } catch {
    // s. o.
  }
  return home
}

/** Ob eine Route zu den Anwendungen des Nutzers passt (Spiegel der Route-Gates). */
function mayEnter(path: string, allowedApplications: string[]): boolean {
  // Ohne bekannte Rechte nicht raten — die Route-Gates weisen sonst ab, und der
  // Nutzer sieht statt seiner Startseite einen Umweg.
  if (allowedApplications.length === 0) return true
  const standalone = ['/chat', '/drive', '/settings', '/tickets', '/harness']
  if (standalone.some((route) => path === route || path.startsWith(`${route}?`))) {
    return allowedApplications.includes('pdb')
  }
  if (path === '/desktop' || path.startsWith('/desktop?') || path.startsWith('/desktop#')) {
    return allowedApplications.includes('webos')
  }
  return true
}
